import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import { User } from 'types/interfaces/user';
import { AdminTransactionHistoryResponse, AdminTransactionHistoryRequest } from 'types/interfaces/transaction';
import {
  CronJobHistoryResponse,
  CronJobHistoryRequest,
  CronJobTypesResponse,
  TriggerCronJobRequest,
  TriggerCronJobResponse
} from 'types/interfaces/cronjob';
import {
  UserDetailsResponse,
  AdjustPointsRequest,
  AdjustPointsResponse,
  ImpersonateUserRequest,
  ImpersonateUserResponse,
  ExitImpersonationResponse,
  BanUserRequest,
  BanUserResponse,
  UnbanUserRequest,
  UnbanUserResponse,
  WriterRoleResponse,
  AdminEliteStatusResponse,
  AdminManageEliteRequest,
  AdminManageEliteResponse
} from 'types/interfaces/admin/userDetails';
import {
  ActiveTraderDiscountSettings,
  UpdateActiveTraderDiscountRequest
} from 'types/interfaces/economic';
import {
  MaintenanceModeSettings,
  UpdateMaintenanceModeRequest,
  UpdateMaintenanceModeResponse
} from 'types/interfaces/admin/maintenanceMode';

export interface AdminUser extends User {
  lastLogin?: string;
  status?: 'active' | 'inactive' | 'pending';
  isAdmin?: boolean;
  isBanned?: boolean;
  isElite?: boolean;
}

export interface UsersListResponse {
  users: AdminUser[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalUsers: number;
    pageSize: number;
  };
}

export interface TradeFeeSettings {
  tradeFeePercentage: number;
  updatedAt: string;
}

export interface UpdateTradeFeeRequest {
  tradeFeePercentage: number;
}

export interface UpdateTradeFeeResponse {
  success: boolean;
  tradeFeePercentage: number;
}

export interface MonthlyFeeSettings {
  monthlyFeePoints: number;
  updatedAt: string;
}

export interface UpdateMonthlyFeeRequest {
  monthlyFeePoints: number;
}

export interface UpdateMonthlyFeeResponse {
  success: boolean;
  monthlyFeePoints: number;
}

export interface UpdateActiveTraderDiscountResponse {
  success: boolean;
  activeTraderDiscountPercentage: number;
}

export const adminApi = createApi({
  reducerPath: 'adminApi',
  baseQuery: baseQuery,
  tagTypes: ['AdminUsers', 'TradeFee', 'MonthlyFee', 'ActiveTraderDiscount', 'MaintenanceMode', 'AdminTransactions', 'CronJobs', 'UserDetails', 'EliteStatus'],
  endpoints: (builder) => ({
    getUsers: builder.query<UsersListResponse, { page?: number; pageSize?: number; search?: string }>({
      query: ({ page = 1, pageSize = 50, search = '' }) => ({
        url: `/admin/users?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['AdminUsers'],
    }),
    getUserById: builder.query<AdminUser, number>({
      query: (userId) => ({
        url: `/admin/users/${userId}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['AdminUsers', 'UserDetails'],
    }),
    getTradeFee: builder.query<TradeFeeSettings, void>({
      query: () => ({
        url: '/admin/settings/trade-fee',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['TradeFee'],
    }),
    updateTradeFee: builder.mutation<UpdateTradeFeeResponse, UpdateTradeFeeRequest>({
      query: (body) => ({
        url: '/admin/settings/trade-fee',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['TradeFee'],
    }),
    getMonthlyFee: builder.query<MonthlyFeeSettings, void>({
      query: () => ({
        url: '/admin/settings/monthly-fee',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['MonthlyFee'],
    }),
    updateMonthlyFee: builder.mutation<UpdateMonthlyFeeResponse, UpdateMonthlyFeeRequest>({
      query: (body) => ({
        url: '/admin/settings/monthly-fee',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['MonthlyFee'],
    }),
    getActiveTraderDiscount: builder.query<ActiveTraderDiscountSettings, void>({
      query: () => ({
        url: '/admin/settings/active-trader-discount',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['ActiveTraderDiscount'],
    }),
    updateActiveTraderDiscount: builder.mutation<UpdateActiveTraderDiscountResponse, UpdateActiveTraderDiscountRequest>({
      query: (body) => ({
        url: '/admin/settings/active-trader-discount',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['ActiveTraderDiscount'],
    }),
    getMaintenanceMode: builder.query<MaintenanceModeSettings, void>({
      query: () => ({
        url: '/admin/settings/maintenance-mode',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['MaintenanceMode'],
    }),
    updateMaintenanceMode: builder.mutation<UpdateMaintenanceModeResponse, UpdateMaintenanceModeRequest>({
      query: (body) => ({
        url: '/admin/settings/maintenance-mode',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['MaintenanceMode'],
    }),
    getAdminTransactions: builder.query<AdminTransactionHistoryResponse, AdminTransactionHistoryRequest>({
      query: ({ page = 1, pageSize = 50, search = '', typeFilter = 'all' }) => ({
        url: `/admin/transactions?page=${page}&pageSize=${pageSize}&search=${encodeURIComponent(search)}&typeFilter=${typeFilter}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['AdminTransactions'],
    }),
    getCronJobHistory: builder.query<CronJobHistoryResponse, CronJobHistoryRequest>({
      query: ({ page = 1, pageSize = 50, typeFilter = 'all', statusFilter = 'all' }) => ({
        url: `/admin/cronjobs?page=${page}&pageSize=${pageSize}&typeFilter=${typeFilter}&statusFilter=${statusFilter}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['CronJobs'],
    }),
    getCronJobTypes: builder.query<CronJobTypesResponse, void>({
      query: () => ({
        url: '/admin/cronjobs/types',
        method: 'GET',
        requiresAuth: true,
      }),
    }),
    triggerCronJob: builder.mutation<TriggerCronJobResponse, { type: string; body: TriggerCronJobRequest }>({
      query: ({ type, body }) => ({
        url: `/admin/cronjobs/${type}/trigger`,
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['CronJobs'],
    }),

    // User Details Admin Panel Endpoints
    getUserDetails: builder.query<UserDetailsResponse, number>({
      query: (userId) => ({
        url: `/admin/users/${userId}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['UserDetails'],
    }),
    adjustUserPoints: builder.mutation<AdjustPointsResponse, { userId: number; body: AdjustPointsRequest }>({
      query: ({ userId, body }) => ({
        url: `/admin/users/${userId}/adjust-points`,
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['UserDetails', 'AdminTransactions'],
    }),
    impersonateUser: builder.mutation<ImpersonateUserResponse, { userId: number; body: ImpersonateUserRequest }>({
      query: ({ userId, body }) => ({
        url: `/admin/users/${userId}/impersonate`,
        method: 'POST',
        body,
        requiresAuth: true,
      }),
    }),
    exitImpersonation: builder.mutation<ExitImpersonationResponse, void>({
      query: () => ({
        url: '/admin/impersonate/exit',
        method: 'POST',
        requiresAuth: true,
      }),
    }),
    banUser: builder.mutation<BanUserResponse, { userId: number; body: BanUserRequest }>({
      query: ({ userId, body }) => ({
        url: `/admin/users/${userId}/ban`,
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['UserDetails', 'AdminUsers'],
    }),
    unbanUser: builder.mutation<UnbanUserResponse, { userId: number; body: UnbanUserRequest }>({
      query: ({ userId, body }) => ({
        url: `/admin/users/${userId}/unban`,
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['UserDetails', 'AdminUsers'],
    }),
    grantWriterRole: builder.mutation<WriterRoleResponse, number>({
      query: (userId) => ({
        url: `/admin/users/${userId}/roles/writer`,
        method: 'POST',
        requiresAuth: true,
      }),
      invalidatesTags: ['UserDetails', 'AdminUsers'],
    }),
    revokeWriterRole: builder.mutation<WriterRoleResponse, number>({
      query: (userId) => ({
        url: `/admin/users/${userId}/roles/writer`,
        method: 'DELETE',
        requiresAuth: true,
      }),
      invalidatesTags: ['UserDetails', 'AdminUsers'],
    }),

    // Elite Status Management
    getAdminEliteStatus: builder.query<AdminEliteStatusResponse, number>({
      query: (userId) => ({
        url: `/admin/users/${userId}/elite`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: (_result, _error, userId) => [{ type: 'EliteStatus', id: userId }],
    }),
    manageUserElite: builder.mutation<AdminManageEliteResponse, { userId: number; body: AdminManageEliteRequest }>({
      query: ({ userId, body }) => ({
        url: `/admin/users/${userId}/elite`,
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        'UserDetails',
        'AdminUsers',
        { type: 'EliteStatus', id: userId },
      ],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useGetTradeFeeQuery,
  useUpdateTradeFeeMutation,
  useGetMonthlyFeeQuery,
  useUpdateMonthlyFeeMutation,
  useGetActiveTraderDiscountQuery,
  useUpdateActiveTraderDiscountMutation,
  useGetMaintenanceModeQuery,
  useUpdateMaintenanceModeMutation,
  useGetAdminTransactionsQuery,
  useGetCronJobHistoryQuery,
  useGetCronJobTypesQuery,
  useTriggerCronJobMutation,
  useGetUserDetailsQuery,
  useAdjustUserPointsMutation,
  useImpersonateUserMutation,
  useExitImpersonationMutation,
  useBanUserMutation,
  useUnbanUserMutation,
  useGrantWriterRoleMutation,
  useRevokeWriterRoleMutation,
  useGetAdminEliteStatusQuery,
  useManageUserEliteMutation
} = adminApi;